import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch, api } from '../lib/api';
import { Card, Btn, SLabel, fmt12 } from '../components/ui';
import { localizeEventTime } from '../lib/tz';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePeople } from '../context/PeopleContext';
import { EventModal, DropZone, CalendarPicker } from './SheetBuilder';
import TravelModal from '../components/TravelModal';
import { useConfirm } from '../components/ConfirmModal';
import useIsMobile from '../hooks/useIsMobile';

function todayStr() { return new Date().toISOString().slice(0,10); }

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0,10);
}

function startOfWeek(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().slice(0,10);
}

function endOfWeek(dateStr) {
  return addDays(startOfWeek(dateStr), 6);
}

// Check if two events overlap in time
function eventsOverlap(a, b) {
  if (!a.timeStart || !b.timeStart) return false;
  const aEnd = a.timeEnd || a.timeStart;
  const bEnd = b.timeEnd || b.timeStart;
  return a.timeStart < bEnd && b.timeStart < aEnd;
}

export default function EventsPage() {
  const isMobile = useIsMobile();
  const { colorMap, team: TEAM } = usePeople();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [confirmAction, ConfirmDialog] = useConfirm();
  const [artists,     setArtists]     = useState([]);
  const [selArtist,   setSelArtist]   = useState('');
  const [allEvents,   setAllEvents]   = useState([]);
  const [gcal,        setGcal]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [gcalLoading, setGcalLoading] = useState(false);
  const [search,      setSearch]      = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  // gcalDate removed — unified into dsDate
  const [addingGcal,  setAddingGcal]  = useState(null);
  const [addingTravel, setAddingTravel] = useState(null);
  const [sheets,      setSheets]      = useState([]);
  const [editEvent,   setEditEvent]   = useState(null);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [travelModal, setTravelModal] = useState(null);

  // Open new event modal from ?action=new (FAB)
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      setShowAddEvent(true);
      navigate('/admin/events', { replace: true });
    }
  }, []);

  // Filters
  const [allArtists,  setAllArtists]  = useState(false); // toggle all artists view
  const [dateFilter,  setDateFilter]  = useState('all'); // all, today, week, next7, custom
  const [customFrom,  setCustomFrom]  = useState('');
  const [customTo,    setCustomTo]    = useState('');
  const [personFilter, setPersonFilter] = useState(''); // '' = all

  // Past events
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [openPastDates, setOpenPastDates] = useState(new Set());

  // Bulk select
  const [bulkMode,    setBulkMode]    = useState(false);
  const [selected,    setSelected]    = useState(new Set());

  // Travel
  const [travelRecords, setTravelRecords] = useState([]);
  const [allTravelForLookup, setAllTravelForLookup] = useState([]);
  const [gcalTravelParsed, setGcalTravelParsed] = useState([]);
  const [gcalTravelLoading, setGcalTravelLoading] = useState(false);
  const [gcalTravelImporting, setGcalTravelImporting] = useState(null);

  // Undo
  const [undoAction,  setUndoAction]  = useState(null); // { label, undo: fn, timer }

  // Day Sheet widget state
  const [dsDate,      setDsDate]      = useState(todayStr());
  const [dsSheet,     setDsSheet]     = useState(null);
  const [dsEvents,    setDsEvents]    = useState([]);
  const [dsLoading,   setDsLoading]   = useState(false);
  const [dsPending,   setDsPending]   = useState([]);
  const [dsSaving,    setDsSaving]    = useState(false);
  const [needsDate,   setNeedsDate]   = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarTab,  setSidebarTab]  = useState('sheet');

  useEffect(() => {
    api.getArtists().then(d => {
      const list = d.artists || [];
      setArtists(list);
      if (list.length) setSelArtist(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selArtist && !allArtists) return;
    setLoading(true);
    if (allArtists && artists.length) {
      // Fetch events from all artists
      Promise.all(artists.map(a => api.getEventsByArtist(a.id).then(d => {
        return (d.events || []).map(e => ({ ...e, sheetDate: e.date, sheetId: e.daysheetId, artistName: a.name, artistEmoji: a.emoji }));
      }).catch(() => []))).then(results => {
        const evts = results.flat();
        evts.sort((a,b) => (b.sheetDate||'').localeCompare(a.sheetDate||'') || (a.timeStart||'').localeCompare(b.timeStart||''));
        setAllEvents(evts);
      }).finally(() => setLoading(false));
      // Fetch all sheets
      Promise.all(artists.map(a => api.getSheets(a.id).then(d => d.sheets || d || []).catch(() => []))).then(results => {
        setSheets(results.flat().sort((a,b) => b.date.localeCompare(a.date)));
      });
    } else if (selArtist) {
      api.getEventsByArtist(selArtist).then(d => {
        const evts = (d.events || []).map(e => ({
          ...e,
          sheetDate: e.date,
          sheetId: e.daysheetId,
        }));
        evts.sort((a,b) => (b.sheetDate||'').localeCompare(a.sheetDate||'') || (a.timeStart||'').localeCompare(b.timeStart||''));
        setAllEvents(evts);
      }).catch(()=>{}).finally(()=>setLoading(false));
      api.getSheets(selArtist).then(d => {
        setSheets((d.sheets || d || []).sort((a,b) => b.date.localeCompare(a.date)));
      }).catch(()=>{});
    }
  }, [selArtist, allArtists, artists]);

  useEffect(() => {
    if (!selArtist || !dsDate) return;
    setGcalLoading(true);
    api.getCalEvents(selArtist, dsDate)
      .then(d => setGcal(d.events||[]))
      .catch(()=>setGcal([]))
      .finally(()=>setGcalLoading(false));
  }, [selArtist, dsDate]);

  // Load travel records for selected date
  useEffect(() => {
    if (!selArtist || !dsDate) { setTravelRecords([]); return; }
    api.getTravel(selArtist, dsDate).then(d => setTravelRecords(Array.isArray(d) ? d : [])).catch(() => setTravelRecords([]));
  }, [selArtist, dsDate]);

  // Load GCal travel for selected date
  useEffect(() => {
    if (!selArtist || !dsDate || gcalTravelImporting) return;
    setGcalTravelLoading(true);
    api.gcalTravel(selArtist, dsDate).then(d => setGcalTravelParsed(d.parsed || [])).catch(() => setGcalTravelParsed([])).finally(() => setGcalTravelLoading(false));
  }, [selArtist, dsDate]); // eslint-disable-line

  // Load all travel for lookup (flight event → travel modal)
  useEffect(() => {
    if (!selArtist) return;
    api.getAllTravel(selArtist).then(d => setAllTravelForLookup(Array.isArray(d) ? d : [])).catch(() => {});
  }, [selArtist]);

  // Load day sheet when date is picked
  useEffect(() => {
    if (!selArtist || !dsDate) { setDsSheet(null); setDsEvents([]); return; }
    setDsLoading(true);
    const sheet = sheets.find(s => s.date === dsDate);
    setDsSheet(sheet || null);
    if (sheet) {
      apiFetch('/sheets/'+sheet.id).then(d => {
        setDsEvents((d.events||[]).sort((a,b) => (a.timeStart||'').localeCompare(b.timeStart||'')));
      }).catch(()=>setDsEvents([])).finally(()=>setDsLoading(false));
    } else {
      setDsEvents([]);
      setDsLoading(false);
    }
  }, [selArtist, dsDate, sheets]);

  const refreshEvents = useCallback(() => {
    api.getEventsByArtist(selArtist).then(d => {
      const evts = (d.events || []).map(e => ({ ...e, sheetDate: e.date, sheetId: e.daysheetId }));
      evts.sort((a,b) => (b.sheetDate||'').localeCompare(a.sheetDate||'') || (a.timeStart||'').localeCompare(b.timeStart||''));
      setAllEvents(evts);
    });
  }, [selArtist]);

  const refreshDsEvents = async () => {
    if (!dsSheet) return;
    const d = await apiFetch('/sheets/'+dsSheet.id);
    setDsEvents((d.events||[]).sort((a,b) => (a.timeStart||'').localeCompare(b.timeStart||'')));
  };

  // Undo helper
  const showUndo = (label, undoFn) => {
    if (undoAction?.timer) clearTimeout(undoAction.timer);
    const timer = setTimeout(() => setUndoAction(null), 5000);
    setUndoAction({ label, undo: undoFn, timer });
  };

  const handleEventTap = (event) => {
    // If it's a flight event with a linked travel record, open the TravelModal
    if (event.travelId && (event.travelType === 'flight_departure' || event.travelType === 'flight_arrival' || event.travelType === 'flight')) {
      const travel = travelRecords.find(t => t.id === event.travelId) || allTravelForLookup.find(t => t.id === event.travelId);
      const leg = event.travelType === 'flight_departure' ? 'departure' : event.travelType === 'flight_arrival' ? 'arrival' : null;
      if (travel) { setTravelModal({ record: travel, leg }); return; }
    }
    setEditEvent({...event});
  };

  const handleQuickAdd = (event) => {
    if (!dsDate) {
      setNeedsDate(true);
      setTimeout(() => setNeedsDate(false), 2000);
      setDsPending(prev => prev.some(p => p.id === event.id) ? prev : [...prev, event].sort((a,b) => (a.timeStart||'99:99').localeCompare(b.timeStart||'99:99')));
      if (isMobile) setShowSidebar(true);
      return;
    }
    // If event is from a different date, duplicate it with the new date
    const eventToAdd = (event.sheetDate && event.sheetDate !== dsDate)
      ? { ...event, id: 'dup-' + event.id + '-' + Date.now(), date: dsDate, sheetDate: dsDate, isNew: true, daysheetId: null }
      : event;
    if (dsEvents.some(e => e.id === eventToAdd.id)) return;
    if (dsPending.some(p => p.title === eventToAdd.title && p.timeStart === eventToAdd.timeStart)) return;
    setDsPending(prev => [...prev, eventToAdd].sort((a,b) => (a.timeStart||'99:99').localeCompare(b.timeStart||'99:99')));
  };

  const handleRemovePending = (eventId) => {
    setDsPending(prev => prev.filter(p => p.id !== eventId));
  };

  const handleRemoveFromSheet = async (eventId) => {
    if (!dsSheet) return;
    try {
      await api.unlinkEvent(eventId, dsSheet.id);
      await refreshDsEvents();
    } catch(e) { alert('Error: ' + e.message); }
  };

  const handleSaveToSheet = async () => {
    if (!dsDate) return;
    setDsSaving(true);
    try {
      let sheet = dsSheet;
      if (!sheet) {
        const res = await api.createSheet({ artistId:selArtist, date:dsDate });
        sheet = res;
        const updated = await api.getSheets(selArtist);
        setSheets((updated.sheets || updated || []).sort((a,b) => b.date.localeCompare(a.date)));
        setDsSheet(sheet);
      }
      for (const e of dsPending) {
        if (e.isNew || e.id?.startsWith('travel-')) {
          // Create the event on the server first
          const created = await api.createEvent({
            artistId: selArtist, date: dsDate,
            title: e.title, timeStart: e.timeStart || null, timeEnd: e.timeEnd || null,
            location: e.location || null, notes: e.notes || null,
            person: e.person || null, travelType: e.travelType || null,
          });
          if (created.id) await api.linkEvent(created.id, sheet.id);
        } else {
          await api.linkEvent(e.id, sheet.id);
        }
      }
      setDsPending([]);
      refreshEvents();
      const d = await apiFetch('/sheets/'+sheet.id);
      setDsEvents((d.events||[]).sort((a,b) => (a.timeStart||'').localeCompare(b.timeStart||'')));
    } catch(e) { alert('Error: ' + e.message); }
    finally { setDsSaving(false); }
  };

  const handleExtractedFiles = async (extractedEvents, pdfFiles) => {
    if (!extractedEvents.length) return;
    try {
      const created = [];
      const fileAttachments = (pdfFiles || []).map(f => ({ name: f.name, type: f.type, data: f.data }));

      // Group flight events by flightNumber → create travel records (backend auto-creates events)
      const flightEvents = extractedEvents.filter(e => e.type === 'flight_departure' || e.type === 'flight_arrival');
      const nonFlightEvents = extractedEvents.filter(e => e.type !== 'flight_departure' && e.type !== 'flight_arrival');

      // Create travel records for flights (grouped by flightNumber)
      const flightsByNum = {};
      flightEvents.forEach(e => {
        const key = e.flightNumber || e.title || Date.now();
        if (!flightsByNum[key]) flightsByNum[key] = {};
        if (e.type === 'flight_departure') flightsByNum[key].dep = e;
        if (e.type === 'flight_arrival') flightsByNum[key].arr = e;
      });

      for (const [, flight] of Object.entries(flightsByNum)) {
        const dep = flight.dep || {};
        const arr = flight.arr || {};
        const artistName = artists.find(a => a.id === selArtist)?.name || '';
        const result = await api.createTravel({
          artistId: selArtist,
          type: 'flight',
          startDate: dep.date || arr.date || dsDate || todayStr(),
          endDate: arr.date || dep.date || dsDate || todayStr(),
          airline: dep.notes?.split(' · ')[0] || arr.notes?.split(' · ')[0] || null,
          flightNumber: dep.flightNumber || arr.flightNumber || null,
          departureAirport: dep.departureAirport || null,
          arrivalAirport: arr.arrivalAirport || dep.arrivalAirport || null,
          flightDepartureTime: dep.timeStart || null,
          flightArrivalTime: arr.timeStart || null,
          passenger: artistName || null,
          seat: null,
          flightConfirmation: null,
          greeterName: null,
          greeterPhone: null,
          files: fileAttachments,
        });
        // Backend auto-created events are in result.events
        if (result.events) created.push(...result.events);
      }

      // Create regular events (non-flight)
      for (const e of nonFlightEvents) {
        const evt = await api.createEvent({
          artistId: selArtist,
          date: e.date || dsDate || todayStr(),
          title: e.title,
          timeStart: e.timeStart || null,
          timeEnd: e.timeEnd || null,
          location: e.location || null,
          notes: e.notes || null,
          person: e.person || null,
          files: e.files || fileAttachments,
        });
        created.push(evt);
      }

      // Auto-link non-flight events to sheets by date
      for (const evt of created) {
        if (!evt.travelId) {
          const sheet = sheets.find(s => s.date === evt.date);
          if (sheet && evt.id) {
            await api.linkEvent(evt.id, sheet.id);
          }
        }
      }
      refreshEvents();
      const travelCount = Object.keys(flightsByNum).length;
      const eventCount = nonFlightEvents.length;
      const msg = [travelCount ? `${travelCount} flight${travelCount!==1?'s':''}` : '', eventCount ? `${eventCount} event${eventCount!==1?'s':''}` : ''].filter(Boolean).join(' + ') || `${created.length} item${created.length!==1?'s':''}`;
      showUndo(`Extracted ${msg}`, async () => {
        await Promise.all(created.map(e => api.deleteEvent(e.id)));
        refreshEvents();
      });
    } catch (e) { alert('Error saving extracted events: ' + e.message); }
  };

  const handleAddTravelAsEvent = async (record) => {
    if (addingTravel === record.id) return; // prevent double-click
    setAddingTravel(record.id);
    const flightNotes = [record.airline, record.flightNumber, record.seat?'Seat: '+record.seat:'', record.flightConfirmation?'Conf: '+record.flightConfirmation:''].filter(Boolean).join(' · ');
    const arrivalNotes = [flightNotes, record.greeterName?'Greeter: '+record.greeterName+(record.greeterPhone?' '+record.greeterPhone:''):''].filter(Boolean).join(' / ');
    const eventsToCreate = [];

    if (record.type === 'flight') {
      eventsToCreate.push({
        title: `${record.passenger||artists.find(a=>a.id===selArtist)?.name||'Flight'} departs ${record.departureAirport||''}`.trim(),
        timeStart: record.flightDepartureTime || null,
        location: record.departureAirport ? record.departureAirport + ' Airport' : null,
        notes: flightNotes || null, person: record.passenger || null, travelType: 'flight',
      });
      eventsToCreate.push({
        title: `${record.passenger||artists.find(a=>a.id===selArtist)?.name||''} arrives ${record.arrivalAirport||''}`.trim(),
        timeStart: record.flightArrivalTime || null,
        location: record.arrivalAirport ? record.arrivalAirport + ' Airport' : null,
        notes: arrivalNotes || null, person: record.passenger || null, travelType: 'flight',
      });
    } else if (record.type === 'hotel') {
      eventsToCreate.push({
        title: `Check in: ${record.hotelName||'Hotel'}`,
        location: record.hotelAddress || null, travelType: 'hotel',
        notes: [record.roomType, record.hotelConfirmation?'Conf: '+record.hotelConfirmation:''].filter(Boolean).join(' · ') || null,
      });
    } else if (record.type === 'ground') {
      eventsToCreate.push({
        title: `${record.driverName||'Driver'} pickup`,
        location: record.pickupLocation || null, travelType: 'ground',
        notes: record.driverPhone ? 'Driver: ' + record.driverPhone : null,
      });
    } else if (record.type === 'carservice') {
      const csTime = (() => { if (!record.carServiceTime) return null; const m = record.carServiceTime.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i); if (!m) return record.carServiceTime; let h=parseInt(m[1]); if (m[3]?.toLowerCase()==='pm'&&h!==12) h+=12; if (m[3]?.toLowerCase()==='am'&&h===12) h=0; return String(h).padStart(2,'0')+':'+m[2]; })();
      eventsToCreate.push({
        title: `${record.carServiceCompany||'Car Service'} pickup`,
        timeStart: csTime, travelType: 'carservice',
        location: record.carServicePickup || null,
        notes: [record.carServiceDriver?'Driver: '+record.carServiceDriver:'', record.carServicePhone||'', record.carServiceConfirmation?'Conf: '+record.carServiceConfirmation:''].filter(Boolean).join(' · ') || null,
      });
    } else if (record.type === 'car') {
      eventsToCreate.push({
        title: `${record.carCompany||'Car'} pickup`,
        location: record.carPickupLocation || null, travelType: 'car',
        notes: record.carConfirmation ? 'Conf: ' + record.carConfirmation : null,
      });
    } else {
      eventsToCreate.push({
        title: `${record.trainCompany||'Train'} ${record.trainFrom||''} → ${record.trainTo||''}`,
        timeStart: record.departureTime || null, location: record.trainFrom || null, travelType: 'train',
        notes: record.trainConfirmation ? 'Conf: ' + record.trainConfirmation : null,
      });
    }

    // Create events directly and link to sheet
    try {
      let sheet = dsSheet;
      if (!sheet) {
        sheet = await api.createSheet({ artistId: selArtist, date: dsDate });
        const updated = await api.getSheets(selArtist);
        setSheets((updated.sheets || updated || []).sort((a,b) => b.date.localeCompare(a.date)));
        setDsSheet(sheet);
      }
      for (const evt of eventsToCreate) {
        const created = await api.createEvent({
          artistId: selArtist, date: dsDate, ...evt,
        });
        if (created.id && sheet.id) await api.linkEvent(created.id, sheet.id);
      }
      refreshEvents();
      const d = await apiFetch('/sheets/'+sheet.id);
      setDsEvents((d.events||[]).sort((a,b) => (a.timeStart||'').localeCompare(b.timeStart||'')));
    } catch(e) { alert('Error adding travel: ' + e.message); }
    finally { setAddingTravel(null); }
  };

  // Detect if a GCal event is travel-related
  const isTravelEvent = (ge) => {
    const text = `${ge.title || ''} ${ge.notes || ''} ${ge.location || ''}`;
    return /\bfly\b|\bflight\b|\bhotel\b|\bcheck.?in\b|\bcar\s*service\b|\bdriver\b|\btrain\b|\bairport\b|\b[A-Z]{3}\s*(?:[-–→]|to)\s*[A-Z]{3}\b/i.test(text)
      || /\bemirates\b|\bunited\b|\bdelta\b|\bamerican\b|\bjetblue\b|\bnetjets\b|\bprivé\b|\bEK\d|\bUA\d|\bDL\d|\bAA\d/i.test(text);
  };

  const handleAddGcalEvent = async (ge) => {
    setAddingGcal(ge.gcalEventId || ge.title);
    try {
      // All-day events go to sheet notes instead of schedule
      if (ge.isAllDay) {
        const sheet = sheets.find(s => s.date === dsDate);
        if (sheet) {
          const existing = sheet.notes || '';
          const noteText = ge.title + (ge.notes ? ' — ' + ge.notes : '');
          const newNotes = existing ? existing + ', ' + noteText : noteText;
          await api.updateSheet(sheet.id, { notes: newNotes });
          setSheets(prev => prev.map(s => s.id === sheet.id ? { ...s, notes: newNotes } : s));
          showUndo(
            `Added "${ge.title}" to notes`,
            async () => {
              await api.updateSheet(sheet.id, { notes: existing });
              setSheets(prev => prev.map(s => s.id === sheet.id ? { ...s, notes: existing } : s));
            }
          );
        } else {
          alert('Create a sheet for this date first to add notes');
        }
        return;
      }

      // Travel events → parse with Claude and create travel record (auto-generates events)
      if (isTravelEvent(ge)) {
        try {
          const parsed = await api.parseGcalEvent({
            title: ge.title, notes: ge.notes, location: ge.location,
            timeStart: ge.timeStart, timeEnd: ge.timeEnd, date: dsDate,
          });
          if (parsed && parsed.type) {
            const body = {
              artistId: selArtist, ...parsed,
              startDate: parsed.startDate || dsDate,
              endDate: parsed.endDate || dsDate,
            };
            const result = await api.createTravel(body);
            // Link auto-created events to the sheet
            const sheet = sheets.find(s => s.date === dsDate);
            if (sheet && result.events?.length) {
              for (const evt of result.events) {
                if (evt.id) await api.linkEvent(evt.id, sheet.id);
              }
            }
            refreshEvents();
            showUndo(
              `Imported "${ge.title}" as travel record`,
              null // Can't easily undo travel creation
            );
            return;
          }
        } catch (e) {
          console.error('Travel parse failed, falling back to regular event:', e);
          // Fall through to create as regular event
        }
      }

      // Regular events
      const event = {
        artistId: selArtist, date: dsDate, title: ge.title,
        timeStart: ge.timeStart || null, timeEnd: ge.timeEnd || null,
        location: ge.location || null, notes: ge.notes || null,
        url: ge.url || null,
        person: null, gcalEventId: ge.gcalEventId || null, importedFrom: 'google_calendar',
      };
      const created = await api.createEvent(event);
      const sheet = sheets.find(s => s.date === dsDate);
      if (sheet && created.id) await api.linkEvent(created.id, sheet.id);
      refreshEvents();
      showUndo(
        sheet ? 'Added & linked to sheet' : 'Added as standalone event',
        async () => { await api.deleteEvent(created.id); refreshEvents(); }
      );
    } catch(e) { alert('Error: ' + e.message); }
    finally { setAddingGcal(null); }
  };

  const handleRemoveGcalEvent = async (gcalEventId) => {
    const evt = allEvents.find(ev => ev.gcalEventId === gcalEventId);
    if (!evt) return;
    try {
      await api.deleteEvent(evt.id);
      refreshEvents();
      showUndo('Removed "' + evt.title + '"', async () => {
        await api.createEvent({ artistId:selArtist, date:evt.date, title:evt.title, timeStart:evt.timeStart, timeEnd:evt.timeEnd, location:evt.location, notes:evt.notes, gcalEventId:evt.gcalEventId, importedFrom:'google_calendar' });
        refreshEvents();
      });
    } catch(err) { alert('Error: ' + err.message); }
  };

  const handleEventSave = async (evtData, calendarId) => {
    const pushToGCal = !!calendarId;
    try {
      if (editEvent) {
        await api.updateEvent(editEvent.id, evtData);
        if (pushToGCal) await api.pushToGCal(editEvent.id, calendarId).catch(err => alert('Event saved but GCal sync failed: ' + err.message));
      } else {
        const created = await api.createEvent({ ...evtData, artistId: selArtist });
        const sheet = sheets.find(s => s.date === evtData.date);
        if (sheet && created.id) await api.linkEvent(created.id, sheet.id);
        if (pushToGCal && created.id) await api.pushToGCal(created.id, calendarId).catch(err => alert('Event created but GCal sync failed: ' + err.message));
      }
      setEditEvent(null);
      setShowAddEvent(false);
      refreshEvents();
    } catch(e) { alert('Error: ' + e.message); }
  };

  // Duplicate event to a new date
  const handleDuplicate = async (event, newDate) => {
    try {
      const created = await api.createEvent({
        artistId: selArtist, date: newDate, title: event.title,
        person: event.person, timeStart: event.timeStart, timeEnd: event.timeEnd,
        location: event.location, notes: event.notes, url: event.url,
        files: event.files, contacts: event.contacts,
      });
      const sheet = sheets.find(s => s.date === newDate);
      if (sheet && created.id) await api.linkEvent(created.id, sheet.id);
      refreshEvents();
      setEditEvent(null);
    } catch(e) { alert('Error: ' + e.message); }
  };

  // Bulk actions
  const handleBulkAddToSheet = () => {
    if (!selected.size) return;
    const events = allEvents.filter(e => selected.has(e.id));
    events.forEach(e => handleQuickAdd(e));
    setBulkMode(false);
    setSelected(new Set());
  };

  const handleBulkDelete = async () => {
    if (!selected.size) return;
    if (!await confirmAction(`Delete ${selected.size} event(s)?`, { title: 'Delete Events', confirmLabel: 'Delete' })) return;
    try {
      await Promise.all([...selected].map(id => api.deleteEvent(id)));
      setBulkMode(false);
      setSelected(new Set());
      refreshEvents();
    } catch(e) { alert('Error: ' + e.message); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // --- Filtering ---
  const today = todayStr();

  let dateFiltered = allEvents;
  if (dateFilter === 'today') {
    dateFiltered = allEvents.filter(e => e.sheetDate === today);
  } else if (dateFilter === 'week') {
    const ws = startOfWeek(today), we = endOfWeek(today);
    dateFiltered = allEvents.filter(e => e.sheetDate >= ws && e.sheetDate <= we);
  } else if (dateFilter === 'next7') {
    const end = addDays(today, 7);
    dateFiltered = allEvents.filter(e => e.sheetDate >= today && e.sheetDate <= end);
  } else if (dateFilter === 'custom' && customFrom && customTo) {
    dateFiltered = allEvents.filter(e => e.sheetDate >= customFrom && e.sheetDate <= customTo);
  }

  let filtered = dateFiltered;
  if (personFilter) {
    filtered = filtered.filter(e => (e.person||'').toLowerCase().includes(personFilter.toLowerCase()));
  }
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(e => e.title?.toLowerCase().includes(q) || e.location?.toLowerCase().includes(q) || e.notes?.toLowerCase().includes(q));
  }

  const grouped = filtered.reduce((acc, e) => {
    const key = e.sheetDate || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort((a,b) => b.localeCompare(a));

  const isOnSheet = (id) => dsEvents.some(e => e.id === id);
  const isPending = (id) => dsPending.some(p => p.id === id);

  // Conflict detection for day sheet widget
  const dsConflicts = new Set();
  for (let i = 0; i < dsEvents.length; i++) {
    for (let j = i + 1; j < dsEvents.length; j++) {
      if (eventsOverlap(dsEvents[i], dsEvents[j])) {
        dsConflicts.add(dsEvents[i].id);
        dsConflicts.add(dsEvents[j].id);
      }
    }
  }

  const inp = { background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'9px 12px', fontSize:13, color:'var(--text)', outline:'none', fontFamily:'inherit' };
  const filterBtn = (label, value) => (
    <button key={value} onClick={() => setDateFilter(value)}
      style={{ padding:'5px 12px', borderRadius:20, border:'1px solid', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700,
        background: dateFilter===value ? 'rgba(79,156,249,0.15)' : 'transparent',
        borderColor: dateFilter===value ? 'var(--blue)' : 'var(--border)',
        color: dateFilter===value ? 'var(--blue)' : 'var(--text3)' }}>
      {label}
    </button>
  );

  const getPersonColor = (person) => {
    const ps = (person||'').split(',').map(p=>p.trim()).filter(p=>p&&p.toLowerCase()!=='everyone');
    if (!ps.length) return null;
    const k = Object.keys(colorMap).find(k=>k.toLowerCase()===ps[0].toLowerCase());
    return k ? colorMap[k] : null;
  };

  return (
    <div style={{ padding: isMobile ? '16px' : '28px 32px', maxWidth:1100, margin:'0 auto' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:8 }}>
        <h1 style={{ fontSize: isMobile ? 22 : 26, fontWeight:900, color:'var(--text)', margin:0 }}>Events</h1>
        {/* GCal toggle and Select hidden — functionality available via tabs below */}
      </div>

      {/* Artist picker — circle avatars */}
      <div style={{ display:'flex', gap:16, marginBottom:20, flexWrap:'wrap', justifyContent:'center' }}>
        {artists.map(a => {
          const isSel = !allArtists && selArtist===a.id;
          return (
            <button key={a.id} onClick={() => { setAllArtists(false); setSelArtist(a.id); }}
              style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, padding:0, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', opacity: isSel ? 1 : 0.6, transition:'all 0.15s', minWidth:68 }}>
              <div style={{ width:60, height:60, borderRadius:'50%', border:`2.5px solid ${isSel ? 'var(--blue)' : 'var(--border)'}`,
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, overflow:'hidden',
                background: a.photo ? 'none' : `${a.color||'var(--card)'}20`,
                boxShadow: isSel ? '0 0 0 3px rgba(79,156,249,0.25)' : 'none', transition:'all 0.15s' }}>
                {a.photo ? <img src={a.photo} alt={a.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji||'🎵')}
              </div>
              <span style={{ fontSize:11, fontWeight:700, color: isSel ? 'var(--blue)' : 'var(--text2)', maxWidth:68, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name?.split(' ')[0]}</span>
            </button>
          );
        })}
      </div>

      {/* Bulk actions bar */}
      {bulkMode && selected.size > 0 && (
        <Card style={{ padding:'10px 16px', marginBottom:12, display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
          <span style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>{selected.size} selected</span>
          <div style={{ display:'flex', gap:8 }}>
            <Btn variant="primary" size="sm" onClick={handleBulkAddToSheet}>Add to Sheet</Btn>
            <Btn variant="ghost" size="sm" onClick={handleBulkDelete} style={{ color:'var(--red, #f87171)' }}>Delete</Btn>
          </div>
        </Card>
      )}

      {/* Undo toast */}
      {undoAction && (
        <div style={{ position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)', background:'var(--card)', border:'1px solid var(--border)', borderRadius:12, padding:'10px 20px', display:'flex', alignItems:'center', gap:12, zIndex:999, boxShadow:'0 4px 20px rgba(0,0,0,0.4)' }}>
          <span style={{ fontSize:13, color:'var(--text)' }}>{undoAction.label}</span>
          <button onClick={async () => { clearTimeout(undoAction.timer); await undoAction.undo(); setUndoAction(null); }}
            style={{ fontSize:13, fontWeight:700, color:'var(--blue)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>
            Undo
          </button>
          <button onClick={() => { clearTimeout(undoAction.timer); setUndoAction(null); }}
            style={{ fontSize:14, color:'var(--text3)', background:'none', border:'none', cursor:'pointer', padding:0 }}>×</button>
        </div>
      )}

      <div style={{ maxWidth:900, margin:'0 auto' }}>

        {/* ── Main content: date-focused ── */}
        <div style={{ display:'none' }}>
          {/* Events list moved to All Events section below */}
          {loading ? (
            <div style={{ textAlign:'center', padding:40, color:'var(--text3)' }}>Loading...</div>
          ) : sortedDates.length === 0 ? (
            <Card style={{ padding:40, textAlign:'center' }}>
              <i className="fa-regular fa-calendar" style={{ fontSize:40, color:'var(--text3)', display:'block', marginBottom:12 }}></i>
              <p style={{ fontSize:16, fontWeight:700, color:'var(--text)', marginBottom:6 }}>
                {search ? 'No events match your search' : 'No events yet'}
              </p>
              <p style={{ fontSize:13, color:'var(--text3)', marginBottom:16, lineHeight:1.5 }}>
                {search ? 'Try a different search term or clear filters.' : 'Import events from Google Calendar or create your first event.'}
              </p>
              {!search && (
                <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
                  <Btn variant="primary" onClick={() => { setEditEvent(null); setShowAddEvent(true); }}>Create Event</Btn>
                  {!isMobile && <Btn variant="ghost" onClick={() => {}}>Import from GCal →</Btn>}
                </div>
              )}
            </Card>
          ) : (() => {
            const todayDate = todayStr();
            const upcomingDates = sortedDates.filter(d => d >= todayDate || d === 'Unknown');
            const pastDates = sortedDates.filter(d => d < todayDate && d !== 'Unknown');
            const renderDateGroup = (date) => (
            <Card key={date} style={{ marginBottom:16, overflow:'hidden', padding:0 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderBottom:'1px solid var(--border)' }}>
                <p style={{ fontSize:13, fontWeight:800, color:'var(--text2)', margin:0 }}>
                  {date === 'Unknown' ? 'No Date' : new Date(date+'T12:00:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'})}
                  {date === todayDate && <span style={{ fontSize:10, fontWeight:700, color:'var(--blue)', marginLeft:8, letterSpacing:1 }}>TODAY</span>}
                </p>
                {grouped[date][0]?.sheetId && (
                  <button onClick={() => navigate('/admin/sheets/'+grouped[date][0].sheetId)}
                    style={{ fontSize:12, color:'var(--blue)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}>
                    View Sheet →
                  </button>
                )}
              </div>
              <div>
                {grouped[date].map((e, i) => {
                  const firstColor = getPersonColor(e.person);
                  const persons = (e.person||'').split(',').map(p=>p.trim()).filter(p=>p&&p.toLowerCase()!=='everyone');
                  const onSheet = isOnSheet(e.id);
                  const pending = isPending(e.id);
                  const isSelected = selected.has(e.id);
                  return (
                    <div key={e.id}
                      onClick={() => bulkMode ? toggleSelect(e.id) : handleEventTap(e)}
                      style={{ display:'flex', alignItems:'stretch', cursor:'pointer', borderBottom: i<grouped[date].length-1?'1px solid var(--border)':'none', transition:'background 0.1s',
                        background: isSelected ? 'rgba(79,156,249,0.08)' : undefined }}
                      onMouseEnter={el => { if (!isSelected) el.currentTarget.style.background='rgba(79,156,249,0.04)'; }}
                      onMouseLeave={el => { if (!isSelected) el.currentTarget.style.background='transparent'; }}>
                      {bulkMode && (
                        <div style={{ display:'flex', alignItems:'center', padding:'0 4px 0 12px' }}>
                          <div style={{ width:18, height:18, borderRadius:4, border: isSelected ? '2px solid var(--blue)' : '2px solid var(--border)', background: isSelected ? 'var(--blue)' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            {isSelected && <span style={{ color:'white', fontSize:12, fontWeight:900 }}>✓</span>}
                          </div>
                        </div>
                      )}
                      <div style={{ width:3, background: firstColor||'var(--border2)', flexShrink:0, borderRadius: bulkMode ? 0 : '3px 0 0 3px' }} />
                      <div style={{ flex:1, padding:'12px 14px', minWidth:0 }}>
                        <p style={{ fontSize:14, fontWeight:700, color:'var(--text)', margin:0 }}>
                          {e.timeStart && (() => {
                            const sheetTz = sheets.find(s => s.id === e.sheetId)?.timezone;
                            const { localTime, sourceLabel } = localizeEventTime(e, sheetTz, fmt12);
                            return (
                              <>
                                <u style={{ color: firstColor||'var(--blue)' }}>{fmt12(localTime || e.timeStart)}</u>
                                {sourceLabel && <span style={{ fontSize:9, fontWeight:700, color:'#b45309', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.4)', borderRadius:8, padding:'1px 5px', marginLeft:4, whiteSpace:'nowrap' }}>{sourceLabel}</span>}
                                <span>: </span>
                              </>
                            );
                          })()}{e.title}
                          {persons.length > 1 && (
                            <span style={{ fontSize:11, fontWeight:700, color: firstColor||'var(--blue)', background:(firstColor||'#3B82F6')+'22', border:'1px solid '+(firstColor||'#3B82F6')+'44', borderRadius:20, padding:'1px 7px', marginLeft:6, verticalAlign:'middle' }}>+{persons.length}</span>
                          )}
                        </p>
                        {(e.location || (allArtists && e.artistName)) && (
                          <p style={{ fontSize:12, color:'var(--text3)', marginTop:3 }}>
                            {allArtists && e.artistEmoji && <span>{e.artistEmoji} {e.artistName}{e.location ? ' · ' : ''}</span>}
                            {e.location && <span><i className="fa-solid fa-location-dot" style={{ fontSize:10, marginRight:3 }}></i> {e.location.split(',')[0]}</span>}
                          </p>
                        )}
                      </div>
                      {!bulkMode && (
                        <div style={{ display:'flex', alignItems:'center', padding:'0 12px 0 0', flexShrink:0 }}>
                          {onSheet ? (
                            <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--green, #3fb950)' }} title="On this sheet"></span>
                          ) : pending ? (
                            <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--yellow, #f59e0b)' }} title="Pending"></span>
                          ) : (
                            <button onClick={(ev) => { ev.stopPropagation(); handleQuickAdd(e); }}
                              style={{ width:26, height:26, borderRadius:'50%', background:'var(--blue)', border:'none', cursor:'pointer', color:'white', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center' }}
                              title="Add to day sheet">
                              +
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
            );
            return (<>
              {upcomingDates.map(renderDateGroup)}
              {pastDates.length > 0 && (
                <div>
                  <button onClick={() => setShowPastEvents(v => !v)}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 16px', borderRadius:10, background:'var(--card)', border:'1px solid var(--border)', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700, color:'var(--text3)', width:'100%', marginBottom:8 }}>
                    <i className={`fa-solid fa-chevron-${showPastEvents?'up':'down'}`} style={{ fontSize:10 }}></i>
                    Past Events ({pastDates.reduce((n,d) => n + (grouped[d]||[]).length, 0)})
                  </button>
                  {showPastEvents && (
                    <div style={{ opacity:0.6 }}>
                      {pastDates.map(date => (
                        <Card key={date} style={{ marginBottom:8, overflow:'hidden', padding:0 }}>
                          <button onClick={() => setOpenPastDates(prev => { const n = new Set(prev); if (n.has(date)) n.delete(date); else n.add(date); return n; })}
                            style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 16px', width:'100%', background:'transparent', border:'none', cursor:'pointer', fontFamily:'inherit' }}>
                            <p style={{ fontSize:13, fontWeight:800, color:'var(--text3)', margin:0 }}>
                              {new Date(date+'T12:00:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'})}
                            </p>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <span style={{ fontSize:11, color:'var(--text3)' }}>{(grouped[date]||[]).length}</span>
                              <i className={`fa-solid fa-chevron-${openPastDates.has(date)?'up':'down'}`} style={{ fontSize:10, color:'var(--text3)' }}></i>
                            </div>
                          </button>
                          {openPastDates.has(date) && (
                            <div style={{ borderTop:'1px solid var(--border)' }}>
                              {(grouped[date]||[]).map((e, i) => {
                                const persons = (e.person||'').split(',').map(p=>p.trim()).filter(p=>p&&p.toLowerCase()!=='everyone');
                                const firstColor = getPersonColor(e.person);
                                return (
                                  <div key={e.id} onClick={() => !bulkMode && handleEventTap(e)}
                                    style={{ display:'flex', alignItems:'stretch', cursor:'pointer', borderBottom: i<(grouped[date]||[]).length-1?'1px solid var(--border)':'none' }}>
                                    <div style={{ width:3, background: firstColor||'var(--border2)', flexShrink:0 }} />
                                    <div style={{ flex:1, padding:'10px 14px' }}>
                                      <p style={{ fontSize:13, fontWeight:700, color:'var(--text)', margin:0 }}>
                                        {e.timeStart && <><u style={{ color: firstColor||'var(--blue)' }}>{fmt12(e.timeStart)}</u>: </>}{e.title}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>);
          })()}
        </div>

        {/* Date picker */}
        <div style={{ marginBottom:16 }}>
          <CalendarPicker value={dsDate} onChange={v=>{ setDsDate(v); setNeedsDate(false); }} />
        </div>

          {/* Action buttons */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
            <button onClick={() => { setEditEvent(null); setShowAddEvent(true); }} style={{ padding:'12px 16px', borderRadius:12, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', border:'1.5px solid transparent', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8, boxShadow:'0 4px 12px rgba(30,64,175,0.25)' }}>
              <i className="fa-solid fa-plus" style={{ fontSize:11 }}></i> Create Event
            </button>
            <div style={{ position:'relative', display:'flex' }}>
              <input type="file" id="events-import-input" accept=".pdf,.png,.jpg,.jpeg,.webp" multiple style={{ display:'none' }}
                onChange={e => { if (e.target.files?.length) { /* existing import handler */ e.target.value = ''; }}} />
              <button onClick={()=> document.getElementById('events-import-input')?.click()} style={{ flex:1, padding:'12px 16px', borderRadius:12, background:'transparent', border:'1.5px dashed rgba(168,85,247,0.4)', color:'#a855f7', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8, boxSizing:'border-box' }}>
                <i className="fa-solid fa-file-import" style={{ fontSize:11 }}></i> Import
              </button>
            </div>
          </div>

          {/* Source toggle buttons */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom: sidebarTab ? 10 : 16 }}>
            {[
              { key:'sheet', label:'Day Sheet', icon:'fa-clipboard', count: dsEvents.length + dsPending.length },
              { key:'gcal', label:'Google Cal', icon:'fa-brands fa-google', count: gcal.length },
              { key:'travel', label:'Travel', icon:'fa-plane', count: travelRecords.length + gcalTravelParsed.filter(p=>!p.alreadyImported).length },
            ].map(t => (
              <button key={t.key} onClick={() => setSidebarTab(sidebarTab===t.key ? '' : t.key)}
                style={{ padding:'12px 16px', borderRadius:12, background: sidebarTab===t.key ? 'rgba(30,64,175,0.12)' : 'var(--card)', border: sidebarTab===t.key ? '1.5px solid rgba(30,64,175,0.4)' : '1.5px solid var(--border)', color: sidebarTab===t.key ? '#1E40AF' : 'var(--text2)', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8, transition:'all 0.15s' }}>
                <i className={`fa-solid ${t.icon}`} style={{ fontSize:11 }}></i> {t.label}
                {t.count > 0 && <span style={{ fontSize:10, fontWeight:700, color:'#1E40AF', background:'rgba(30,64,175,0.15)', padding:'2px 7px', borderRadius:8 }}>{t.count}</span>}
                <i className={`fa-solid fa-chevron-${sidebarTab===t.key ? 'up' : 'down'}`} style={{ fontSize:9, marginLeft:'auto', opacity:0.6 }}></i>
              </button>
            ))}
          </div>

          {/* Dropdown panel */}
          {sidebarTab && <Card style={{ padding:0, overflow:'hidden', marginBottom:16, borderTop:'2px solid rgba(30,64,175,0.4)' }}>
            <div style={{ padding:16 }}>

            {/* ── Sheet tab ── */}
            {sidebarTab === 'sheet' && (<>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
              <span style={{ fontSize:11, color:'var(--text3)', fontWeight:600 }}>
                {dsSheet ? (dsSheet.status==='published' ? '● Published' : '○ Draft') : 'No sheet'}
                {dsEvents.length > 0 ? ` · ${dsEvents.length} event${dsEvents.length!==1?'s':''}` : ''}
              </span>
              {dsSheet && (
                <button onClick={() => navigate(`/admin?date=${dsDate}&artist=${selArtist}`)}
                  style={{ fontSize:11, color:'var(--blue)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}>
                  Open →
                </button>
              )}
            </div>
            {needsDate && !dsDate && (
              <p style={{ fontSize:12, color:'var(--blue)', textAlign:'center', marginBottom:8, fontWeight:600 }}>Pick a date to add events to a sheet</p>
            )}
            {dsLoading ? (
              <p style={{ color:'var(--text3)', textAlign:'center', padding:'12px 0', fontSize:13 }}>Loading...</p>
            ) : dsDate && !dsSheet && dsPending.length === 0 ? (
              <div style={{ textAlign:'center', padding:'12px 0' }}>
                <p style={{ color:'var(--text3)', fontSize:13 }}>No sheet for this date</p>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                {dsEvents.map(e => {
                  const pColor = getPersonColor(e.person);
                  const hasConflict = dsConflicts.has(e.id);
                  const tIcons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
                  const tColors = { flight:'var(--blue)', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
                  const tIcon = e.travelType ? tIcons[e.travelType] : null;
                  const tColor = e.travelType ? tColors[e.travelType] : null;
                  return (
                    <div key={e.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 8px', borderRadius:8,
                      background: hasConflict ? 'rgba(248,113,113,0.08)' : 'var(--bg)',
                      border: hasConflict ? '1px solid rgba(248,113,113,0.4)' : '1px solid var(--border)' }}>
                      {tIcon ? (
                        <i className={`fa-solid ${tIcon}`} style={{ fontSize:11, color:tColor, width:14, textAlign:'center', flexShrink:0 }}></i>
                      ) : (
                        <div style={{ width:3, height:20, borderRadius:2, background: pColor||'var(--border2)', flexShrink:0 }} />
                      )}
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ fontSize:12, fontWeight:700, color:'var(--text)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {e.timeStart ? fmt12(e.timeStart)+' ' : ''}{e.title}
                        </p>
                        {hasConflict && <p style={{ fontSize:10, color:'var(--red, #f87171)', margin:'2px 0 0', fontWeight:600 }}>Time conflict</p>}
                      </div>
                      <button onClick={() => handleRemoveFromSheet(e.id)}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:14, padding:'0 4px', flexShrink:0, lineHeight:1 }}
                        title="Remove from sheet">×</button>
                    </div>
                  );
                })}
                {dsPending.map(e => {
                  const pColor = getPersonColor(e.person);
                  const tIcons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
                  const tColors = { flight:'var(--blue)', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
                  return (
                    <div key={e.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 8px', borderRadius:8, background:'rgba(79,156,249,0.06)', border:'1px dashed var(--blue)' }}>
                      {e.travelType ? (
                        <i className={`fa-solid ${tIcons[e.travelType]}`} style={{ fontSize:11, color:tColors[e.travelType], width:14, textAlign:'center', flexShrink:0 }}></i>
                      ) : (
                        <div style={{ width:3, height:20, borderRadius:2, background: pColor||'var(--blue)', flexShrink:0 }} />
                      )}
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ fontSize:12, fontWeight:700, color:'var(--text)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {e.timeStart ? fmt12(e.timeStart)+' ' : ''}{e.title}
                        </p>
                      </div>
                      <button onClick={() => handleRemovePending(e.id)}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red, #f87171)', fontSize:14, padding:'0 4px', flexShrink:0, lineHeight:1 }}
                        title="Cancel">×</button>
                    </div>
                  );
                })}
              </div>
            )}
            {dsPending.length > 0 && dsDate && (
              <div style={{ marginTop:12, display:'flex', gap:8 }}>
                <Btn variant="ghost" size="sm" onClick={() => setDsPending([])} style={{ flex:1 }}>Clear</Btn>
                <Btn variant="primary" size="sm" onClick={handleSaveToSheet} disabled={dsSaving} style={{ flex:1 }}>
                  {dsSaving ? 'Saving...' : `Add ${dsPending.length} to Sheet`}
                </Btn>
              </div>
            )}
            </>)}

            {/* ── GCal tab ── */}
            {sidebarTab === 'gcal' && (<>

          {/* Google Calendar */}
            {gcalLoading ? (
              <p style={{ color:'var(--text3)', textAlign:'center', padding:'16px 0', fontSize:13 }}>Loading...</p>
            ) : gcal.length === 0 ? (
              <p style={{ color:'var(--text3)', textAlign:'center', padding:'16px 0', fontSize:13 }}>No events for this date</p>
            ) : gcal.map((e, i) => {
              const isTravel = !e.isAllDay && isTravelEvent(e);
              const alreadyAdded = e.isAllDay
                ? sheets.find(s => s.date === dsDate)?.notes?.includes(e.title)
                : isTravel
                  ? (travelRecords.some(t => {
                      const titleUp = (e.title||'').toUpperCase();
                      if (t.type==='flight' && t.departureAirport && titleUp.includes(t.departureAirport) && titleUp.includes(t.arrivalAirport)) return true;
                      return false;
                    }) || allEvents.some(ev => ev.gcalEventId === e.gcalEventId))
                  : allEvents.some(ev => ev.gcalEventId === e.gcalEventId);
              const bgColor = e.isAllDay ? 'rgba(245,158,11,0.06)' : isTravel ? 'rgba(30,64,175,0.06)' : 'var(--bg)';
              const borderColor = e.isAllDay ? 'rgba(245,158,11,0.2)' : isTravel ? 'rgba(30,64,175,0.2)' : 'var(--border)';
              return (
                <div key={i} style={{ padding:'10px 12px', borderRadius:10, background: bgColor,
                  border: `1px solid ${borderColor}`, marginBottom:8, display:'flex', alignItems:'center', gap:10, opacity: alreadyAdded ? 0.5 : 1 }}>
                  {e.isAllDay && <i className="fa-solid fa-thumbtack" style={{ fontSize:11, color:'#f59e0b', flexShrink:0 }}></i>}
                  {isTravel && <i className="fa-solid fa-plane" style={{ fontSize:11, color:'var(--blue)', flexShrink:0 }}></i>}
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ fontSize:13, fontWeight:700, color:'var(--text)', marginBottom:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.title}</p>
                    <p style={{ fontSize:11, color:'var(--text3)' }}>
                      {e.isAllDay ? 'All day → adds to notes' : isTravel ? (e.timeStart ? fmt12(e.timeStart) + ' · ' : '') + 'imports as travel record' : e.timeStart ? fmt12(e.timeStart) : 'No time'}
                      {e.calendarName ? ' · '+e.calendarName : ''}
                    </p>
                  </div>
                  {alreadyAdded ? (
                    <span style={{ fontSize:11, fontWeight:700, color:'var(--green, #3fb950)', flexShrink:0 }}>
                      <i className="fa-solid fa-check" style={{ fontSize:9, marginRight:3 }}></i>{isTravel ? 'Imported' : 'Added'}
                    </span>
                  ) : (
                    <button onClick={() => handleAddGcalEvent(e)} disabled={addingGcal===e.gcalEventId||addingGcal===e.title}
                      style={{ width:28, height:28, borderRadius:'50%', background: addingGcal===e.gcalEventId||addingGcal===e.title ? 'var(--text3)' : 'var(--blue)', border:'none', cursor: addingGcal ? 'wait' : 'pointer', color:'white', fontSize:18, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      {addingGcal===e.gcalEventId||addingGcal===e.title ? '···' : '+'}
                    </button>
                  )}
                </div>
              );
            })}
            </>)}

            {/* ── Travel tab ── */}
            {sidebarTab === 'travel' && (<>

            {/* GCal detected travel */}
            {gcalTravelParsed.length > 0 && (
              <div style={{ marginBottom:12 }}>
                <p style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', marginBottom:8 }}>
                  <i className="fa-brands fa-google" style={{ marginRight:4, fontSize:9 }}></i>CALENDAR TRAVEL
                </p>
                {gcalTravelParsed.map((p, i) => {
                  const tIcons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
                  const tColors = { flight:'var(--blue)', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
                  const title = p.type==='flight' ? `${p.airline||''} ${p.flightNumber||''}`.trim()||'Flight' : p.hotelName||p.carServiceCompany||p.driverName||'Travel';
                  const route = p.type==='flight' ? `${p.departureAirport||'?'} → ${p.arrivalAirport||'?'}` : '';
                  return (
                    <div key={p.gcalEventId||i} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:10, background:'rgba(30,64,175,0.06)', border:'1px solid rgba(30,64,175,0.15)', marginBottom:6, opacity: p.alreadyImported ? 0.5 : 1 }}>
                      <i className={`fa-solid ${tIcons[p.type]||'fa-plane'}`} style={{ fontSize:12, color:tColors[p.type]||'var(--blue)', width:16, textAlign:'center', flexShrink:0 }}></i>
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ fontSize:12, fontWeight:700, color:'var(--text)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{title}{route ? <span style={{ color:'var(--blue)', marginLeft:4 }}>{route}</span> : ''}</p>
                        <p style={{ fontSize:10, color:'var(--text3)', margin:0 }}>from calendar</p>
                      </div>
                      {p.alreadyImported ? (
                        <span style={{ fontSize:10, fontWeight:700, color:'#3fb950', flexShrink:0 }}><i className="fa-solid fa-check" style={{ fontSize:8, marginRight:3 }}></i>Imported</span>
                      ) : (
                        <button onClick={async () => {
                          setGcalTravelImporting(p.gcalEventId || i);
                          try {
                            const body = { artistId: selArtist, type: p.type||'flight', startDate: p.startDate||dsDate, endDate: p.endDate||dsDate,
                              airline:p.airline||null, flightNumber:p.flightNumber||null, departureAirport:p.departureAirport||null, arrivalAirport:p.arrivalAirport||null,
                              flightDepartureTime:p.flightDepartureTime||null, flightArrivalTime:p.flightArrivalTime||null, seat:p.seat||null, flightConfirmation:p.flightConfirmation||null,
                              passenger:p.passenger||null, flightType:p.flightType||'commercial', tailNumber:p.tailNumber||null, aircraftType:p.aircraftType||null, operator:p.operator||null,
                              hotelName:p.hotelName||null, hotelAddress:p.hotelAddress||null, hotelConfirmation:p.hotelConfirmation||null,
                              carServiceCompany:p.carServiceCompany||null, carServiceDriver:p.carServiceDriver||null, carServicePhone:p.carServicePhone||null,
                              carServicePickup:p.carServicePickup||null, carServiceDropoff:p.carServiceDropoff||null,
                              driverName:p.driverName||null, driverPhone:p.driverPhone||null, notes:p.notes||null };
                            const result = await api.createTravel(body);
                            if (result?.id) {
                              setTravelRecords(prev => [...prev, result]);
                              setGcalTravelParsed(prev => prev.map(g => (g.gcalEventId||g)===(p.gcalEventId||p) ? {...g, alreadyImported:true} : g));
                              // Link auto-events to sheet
                              const sheet = sheets.find(s => s.date === dsDate);
                              if (sheet && result.events?.length) {
                                for (const evt of result.events) { if (evt.id) await api.linkEvent(evt.id, sheet.id); }
                                refreshEvents();
                              }
                            }
                          } catch(e) { console.error('GCal travel import:', e); }
                          finally { setGcalTravelImporting(null); }
                        }}
                          disabled={gcalTravelImporting && gcalTravelImporting === (p.gcalEventId||i)}
                          style={{ width:22, height:22, borderRadius:'50%', background: (gcalTravelImporting && gcalTravelImporting===(p.gcalEventId||i)) ? 'var(--text3)' : 'var(--blue)', border:'none', cursor:'pointer', color:'white', fontSize:12, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          {(gcalTravelImporting && gcalTravelImporting===(p.gcalEventId||i)) ? '···' : '+'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {gcalTravelLoading && <p style={{ fontSize:11, color:'var(--text3)', textAlign:'center', padding:'8px 0' }}><i className="fa-brands fa-google" style={{ marginRight:4 }}></i>Scanning calendar...</p>}

            {/* Existing travel records */}
            {travelRecords.length === 0 && gcalTravelParsed.length === 0 ? (
              <p style={{ color:'var(--text3)', textAlign:'center', padding:'16px 0', fontSize:13 }}>No travel for this date. <a href="/admin/travel" style={{ color:'var(--blue)' }}>Add →</a></p>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {travelRecords.slice(0, 10).map(r => {
                  const icons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
                  const colors = { flight:'var(--blue)', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
                  const artistName = artists.find(a=>a.id===selArtist)?.name;
                  const label = r.type==='flight'?`${r.passenger||artistName||'Flight'} ${r.departureAirport||''} to ${r.arrivalAirport||''}`.trim()
                    :r.type==='carservice'?r.carServiceCompany||'Car Service'
                    :r.type==='hotel'?r.hotelName||'Hotel'
                    :r.type==='car'?r.carCompany||'Car'
                    :r.type==='ground'?r.driverName||'Ground'
                    :r.trainCompany||'Train';
                  const sub = r.type==='flight'?`${r.airline||''} ${r.flightNumber||''}`.trim()
                    :r.type==='carservice'?`${r.carServiceDriver||''} ${r.carServiceTime?'· '+r.carServiceTime:''}`.trim()
                    :r.type==='hotel'?(r.startDate||'')
                    :r.startDate||'';
                  const allAndPending = [...allEvents, ...dsPending, ...dsEvents];
                  const alreadyAdded = allAndPending.some(e => {
                    // Match by travelId if available
                    if (e.travelId === r.id) return true;
                    // Fallback: match by title content
                    const t = (e.title||'').toLowerCase();
                    if (r.type === 'flight') {
                      const dep = (r.departureAirport||'').toLowerCase();
                      const arr = (r.arrivalAirport||'').toLowerCase();
                      return (dep && (t.includes('departs '+dep) || t.includes(dep+' to '))) || (arr && (t.includes('arrives '+arr) || t.includes('to '+arr)));
                    }
                    if (r.type === 'carservice') return t.includes((r.carServiceCompany||'___').toLowerCase());
                    if (r.type === 'hotel') return t.includes('check in') && t.includes((r.hotelName||'___').toLowerCase());
                    return t.includes((r.driverName||r.carCompany||r.trainCompany||'___NOMATCH').toLowerCase());
                  });
                  return (
                    <div key={r.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:10, background:'var(--bg)', border:'1px solid var(--border)' }}>
                      <i className={`fa-solid ${icons[r.type]||'fa-suitcase'}`} style={{ fontSize:12, color:colors[r.type]||'var(--text3)', width:16, textAlign:'center', flexShrink:0 }}></i>
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ fontSize:12, fontWeight:700, color:'var(--text)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</p>
                        <p style={{ fontSize:10, color:'var(--text3)', margin:0 }}>{sub}</p>
                      </div>
                      {alreadyAdded ? (
                        <button onClick={async () => {
                          // Remove matching events from pending state
                          setDsPending(prev => prev.filter(p => {
                            if (r.type==='flight') return !(p.title?.includes('departs '+r.departureAirport) || p.title?.includes('arrives '+r.arrivalAirport));
                            if (r.type==='carservice') return !p.title?.includes(r.carServiceCompany||'___');
                            return !p.title?.includes(r.hotelName||r.carCompany||r.driverName||r.trainCompany||'___');
                          }));
                          // Also delete matching events from backend
                          const matching = allEvents.filter(e => {
                            if (r.type==='flight') return e.title?.includes('departs '+r.departureAirport) || e.title?.includes('arrives '+r.arrivalAirport);
                            if (r.type==='carservice') return e.title?.includes(r.carServiceCompany||'___');
                            return e.title?.includes(r.hotelName||r.carCompany||r.driverName||r.trainCompany||'___NOMATCH');
                          });
                          try {
                            await Promise.all(matching.map(e => api.deleteEvent(e.id)));
                            refreshEvents();
                          } catch(err) { console.error(err); }
                        }}
                          style={{ fontSize:10, fontWeight:700, color:'#22c55e', background:'none', border:'none', cursor:'pointer', flexShrink:0, fontFamily:'inherit' }}>
                          Added ×
                        </button>
                      ) : (
                        <button onClick={async (ev) => { ev.stopPropagation(); await handleAddTravelAsEvent(r); }}
                          disabled={addingTravel === r.id}
                          style={{ width:22, height:22, borderRadius:'50%', background: addingTravel===r.id ? 'var(--text3)' : 'var(--blue)', border:'none', cursor: addingTravel===r.id ? 'wait' : 'pointer', color:'white', fontSize:12, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          {addingTravel === r.id ? '···' : '+'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            </>)}

            </div>
          </Card>}

        {/* ── All Events widget ── */}
        <Card style={{ padding:0, marginTop:16, overflow:'hidden' }}>
          <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <p style={{ fontSize:11, fontWeight:700, letterSpacing:'1.5px', color:'var(--text3)', margin:0 }}>
              <i className="fa-solid fa-list" style={{ marginRight:5, fontSize:10 }}></i>ALL EVENTS
            </p>
            <span style={{ fontSize:11, color:'var(--text3)', fontWeight:600 }}>{allEvents.length}</span>
          </div>
          <div style={{ padding:'10px 14px 8px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10 }}>
            <i className="fa-solid fa-magnifying-glass" style={{ fontSize:13, color:'var(--text3)', flexShrink:0 }}></i>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search events..."
              style={{ flex:1, background:'transparent', border:'none', fontSize:13, color:'var(--text)', outline:'none', fontFamily:'inherit', padding:0 }} />
            {search && (
              <button onClick={() => setSearch('')}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:16, padding:0, lineHeight:1, flexShrink:0 }}>×</button>
            )}
          </div>
          {(() => {
            const todayDate = todayStr();
            const eventsToShow = filtered;
            if (loading) return <p style={{ color:'var(--text3)', textAlign:'center', padding:'16px 0', fontSize:13 }}>Loading...</p>;
            if (eventsToShow.length === 0) return <p style={{ color:'var(--text3)', textAlign:'center', padding:'16px 0', fontSize:13 }}>{search ? 'No matches' : 'No events'}</p>;

            // When searching, group identical events by title+time to reduce clutter
            if (search) {
              const groups = {};
              eventsToShow.forEach(e => {
                const key = `${(e.title||'').toLowerCase().trim()}::${e.timeStart||''}::${(e.person||'').toLowerCase().trim()}`;
                if (!groups[key]) groups[key] = [];
                groups[key].push(e);
              });
              // Sort groups: most recent first within each, then groups by most recent occurrence
              const groupList = Object.values(groups).map(g => {
                g.sort((a,b) => (b.sheetDate||'').localeCompare(a.sheetDate||''));
                return g;
              }).sort((a,b) => (b[0].sheetDate||'').localeCompare(a[0].sheetDate||''));

              const renderEventRow = (e, idx, total, dimmed) => {
                const firstColor = getPersonColor(e.person);
                const onSheet = isOnSheet(e.id);
                const pending = isPending(e.id);
                return (
                  <div key={e.id} onClick={() => handleEventTap(e)}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', cursor:'pointer',
                      borderBottom: idx<total-1 ? '1px solid var(--border)' : 'none', opacity: dimmed ? 0.6 : 1 }}
                    onMouseEnter={el => el.currentTarget.style.background='rgba(79,156,249,0.04)'}
                    onMouseLeave={el => el.currentTarget.style.background='transparent'}>
                    <div style={{ width:3, height:22, borderRadius:2, background: firstColor||'var(--border2)', flexShrink:0 }} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:13, fontWeight:700, color:'var(--text)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {e.timeStart && <><span style={{ color: firstColor||'var(--blue)' }}>{fmt12(e.timeStart)}</span> </>}{e.title}
                      </p>
                    </div>
                    <span style={{ fontSize:10, color:'var(--text3)', flexShrink:0, marginRight:4 }}>
                      {e.sheetDate ? new Date(e.sheetDate+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'}) : ''}
                    </span>
                    {onSheet ? (
                      <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--green)', flexShrink:0 }} title="On sheet"></span>
                    ) : pending ? (
                      <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--yellow, #f59e0b)', flexShrink:0 }} title="Pending"></span>
                    ) : (
                      <button onClick={(ev) => { ev.stopPropagation(); handleQuickAdd(e); }}
                        style={{ width:22, height:22, borderRadius:'50%', background:'var(--blue)', border:'none', cursor:'pointer', color:'white', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
                        title="Add to day sheet">+</button>
                    )}
                    <button onClick={async (ev) => { ev.stopPropagation(); if (window.confirm('Delete this event permanently?')) { try { await api.deleteEvent(e.id); refreshEvents(); } catch(err) { alert(err.message); } } }}
                      style={{ width:22, height:22, borderRadius:'50%', background:'rgba(248,81,73,0.1)', border:'1px solid rgba(248,81,73,0.25)', cursor:'pointer', color:'#f85149', fontSize:12, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, padding:0 }}
                      title="Delete event">×
                    </button>
                  </div>
                );
              };

              return (
                <div style={{ maxHeight:400, overflowY:'auto' }}>
                  {groupList.map((group, gi) => {
                    const e = group[0];
                    const count = group.length;
                    const groupKey = `${(e.title||'')}::${e.timeStart||''}::${(e.person||'')}`;
                    const isExpanded = expandedGroups.has(groupKey);
                    const firstColor = getPersonColor(e.person);
                    const onSheet = isOnSheet(e.id);
                    const pending = isPending(e.id);
                    return (
                      <div key={groupKey} style={{ borderBottom: gi<groupList.length-1 ? '1px solid var(--border)' : 'none' }}>
                        {/* Group header row */}
                        <div onClick={() => handleEventTap(e)}
                          style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', cursor:'pointer' }}
                          onMouseEnter={el => el.currentTarget.style.background='rgba(79,156,249,0.04)'}
                          onMouseLeave={el => el.currentTarget.style.background='transparent'}>
                          <div style={{ width:3, height:22, borderRadius:2, background: firstColor||'var(--border2)', flexShrink:0 }} />
                          <div style={{ flex:1, minWidth:0 }}>
                            <p style={{ fontSize:13, fontWeight:700, color:'var(--text)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                              {e.timeStart && <><span style={{ color: firstColor||'var(--blue)' }}>{fmt12(e.timeStart)}</span> </>}{e.title}
                            </p>
                            {e.person && <p style={{ fontSize:10, color:'var(--text3)', margin:'1px 0 0' }}>{e.person}</p>}
                          </div>
                          {count > 1 && (
                            <button onClick={ev => { ev.stopPropagation(); setExpandedGroups(prev => { const n = new Set(prev); if (n.has(groupKey)) n.delete(groupKey); else n.add(groupKey); return n; }); }}
                              style={{ fontSize:10, fontWeight:700, color: isExpanded ? 'var(--blue)' : 'var(--text3)', flexShrink:0, padding:'2px 8px',
                                background: isExpanded ? 'rgba(79,156,249,0.1)' : 'var(--bg)', borderRadius:8, border:'1px solid '+(isExpanded ? 'var(--blue)' : 'var(--border)'),
                                cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s', display:'flex', alignItems:'center', gap:3 }}>
                              ×{count}
                              <i className={`fa-solid fa-chevron-${isExpanded?'up':'down'}`} style={{ fontSize:7 }}></i>
                            </button>
                          )}
                          <span style={{ fontSize:10, color:'var(--text3)', flexShrink:0, marginRight:4 }}>
                            {e.sheetDate ? new Date(e.sheetDate+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'}) : ''}
                          </span>
                          <button onClick={async (ev) => { ev.stopPropagation(); if (window.confirm('Delete this event?')) { try { await Promise.all(group.map(g => api.deleteEvent(g.id))); refreshEvents(); } catch(err) { alert(err.message); } } }}
                            style={{ width:22, height:22, borderRadius:'50%', background:'rgba(248,81,73,0.1)', border:'1px solid rgba(248,81,73,0.2)', cursor:'pointer', color:'#f85149', fontSize:11, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
                            title="Delete travel">×</button>
                          {onSheet ? (
                            <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--green)', flexShrink:0 }} title="On sheet"></span>
                          ) : pending ? (
                            <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--yellow, #f59e0b)', flexShrink:0 }} title="Pending"></span>
                          ) : (
                            <button onClick={(ev) => { ev.stopPropagation(); handleQuickAdd(e); }}
                              style={{ width:22, height:22, borderRadius:'50%', background:'var(--blue)', border:'none', cursor:'pointer', color:'white', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
                              title="Add to day sheet">+</button>
                          )}
                        </div>
                        {/* Expanded: show all occurrences */}
                        {isExpanded && count > 1 && (
                          <div style={{ background:'var(--bg)', borderTop:'1px solid var(--border)' }}>
                            {group.map((sub, si) => renderEventRow(sub, si, group.length, sub.sheetDate && sub.sheetDate < todayDate))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            }

            // Normal view (no search) — flat list
            return (
              <div style={{ maxHeight:400, overflowY:'auto' }}>
                {eventsToShow.map((e, i) => {
                  const firstColor = getPersonColor(e.person);
                  const isPast = e.sheetDate && e.sheetDate < todayDate;
                  const onSheet = isOnSheet(e.id);
                  const pending = isPending(e.id);
                  return (
                    <div key={e.id} onClick={() => handleEventTap(e)}
                      style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', cursor:'pointer', borderBottom: i<eventsToShow.length-1 ? '1px solid var(--border)' : 'none', opacity: isPast ? 0.5 : 1 }}
                      onMouseEnter={el => el.currentTarget.style.background='rgba(79,156,249,0.04)'}
                      onMouseLeave={el => el.currentTarget.style.background='transparent'}>
                      <div style={{ width:3, height:22, borderRadius:2, background: firstColor||'var(--border2)', flexShrink:0 }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ fontSize:13, fontWeight:700, color:'var(--text)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {e.timeStart && <><span style={{ color: firstColor||'var(--blue)' }}>{fmt12(e.timeStart)}</span> </>}{e.title}
                        </p>
                      </div>
                      <span style={{ fontSize:10, color:'var(--text3)', flexShrink:0, marginRight:4 }}>
                        {e.sheetDate ? new Date(e.sheetDate+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'}) : ''}
                      </span>
                      {onSheet ? (
                        <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--green)', flexShrink:0 }} title="On sheet"></span>
                      ) : pending ? (
                        <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--yellow, #f59e0b)', flexShrink:0 }} title="Pending"></span>
                      ) : (
                        <button onClick={(ev) => { ev.stopPropagation(); handleQuickAdd(e); }}
                          style={{ width:22, height:22, borderRadius:'50%', background:'var(--blue)', border:'none', cursor:'pointer', color:'white', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}
                          title="Add to day sheet">+</button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </Card>

        </div>{/* close maxWidth:560 */}

      {/* Event Modal — Add, Edit, or Duplicate */}
      {(showAddEvent || editEvent) && (
        <EventModal
          event={editEvent || null}
          onSave={(data) => handleEventSave(data, null)}
          onSaveAndSync={(data, calendarId) => handleEventSave(data, calendarId)}
          onDuplicate={editEvent ? handleDuplicate : undefined}
          onDelete={editEvent ? async (evt) => {
            try {
              await api.deleteEvent(evt.id);
              setEditEvent(null);
              refreshEvents();
              showUndo(`Deleted "${evt.title}"`, async () => {
                await api.createEvent({ artistId: selArtist, date: evt.date, title: evt.title, timeStart: evt.timeStart, timeEnd: evt.timeEnd, location: evt.location, notes: evt.notes, person: evt.person });
                refreshEvents();
              });
            } catch(e) { alert('Error: ' + e.message); }
          } : undefined}
          onClose={() => { setEditEvent(null); setShowAddEvent(false); }}
          sheetArtistId={selArtist}
        />
      )}

      {/* Travel Modal — for flight events */}
      <TravelModal record={travelModal?.record} focusLeg={travelModal?.leg} onClose={() => setTravelModal(null)} />
      {ConfirmDialog}
    </div>
  );
}
